"use client";
import type { ReactNode } from "react";
import { useSession } from "next-auth/react";
import StepTime from "./step-time";
import { type RouterOutput } from "~/server/api/root";
import StepLogin, { StepLoginAuthorised } from "./step-login";
import StepPayment from "./step-payment";
import StepPaymentReturn from "./step-payment-return";
import StepIntroCallConfirm from "./step-intro-call-confirm";
import { PaymentConfirmationLoadingShell } from "../payment-confirmation";
import type { BookingAttribution } from "./booking-attribution";
import { ViewItemTracking } from "../view-item-tracking";
import { Skeleton } from "../ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../ui/breadcrumb";

import { Armchair, Check, ChevronLeft, Sofa } from "lucide-react";
import Link from "next/link";
import { Button, buttonVariants } from "../ui/button";
import { TypographyH1, TypographyMuted } from "../ui/typography";
import {
  TherapyRelationshipType,
  TherapySessionPurpose,
} from "~/prisma/browser";
import { addHours, format, isToday, isTomorrow } from "date-fns";
import { srLatn } from "date-fns/locale";
import { toZonedTime } from "date-fns-tz";
import { Suspense, use, useMemo, useEffect, useState } from "react";
import { cn } from "~/lib/utils";

import TimeWarning from "./time-warning";
import {
  parseAsBoolean,
  parseAsInteger,
  parseAsIsoDateTime,
  parseAsStringEnum,
  useQueryState,
} from "nuqs";
import { useResolvedMediaQuery } from "../patterns/stepper/use-media-query";
import { BookingSummary } from "./booking-summary";

interface SessionBookingProps {
  therapist: NonNullable<RouterOutput["therapistRouter"]["getTherapist"]>;
  initialStep: number;
  attribution: Promise<BookingAttribution>;
  therapyType: TherapyRelationshipType;
  sessionPurpose?: TherapySessionPurpose;
  date?: Date;
  time?: string;
}

interface BookingStep {
  label: string;
  content: ReactNode;
}

const MIN_STEP = 0;
const MAX_STEP = 3;
const VALID_STEP_QUERY_VALUES = new Set(["0", "1", "2", "3"]);

function clampStep(step: number): number {
  if (!Number.isInteger(step) || step < MIN_STEP || step > MAX_STEP) {
    return MIN_STEP;
  }
  return step;
}

function hasValidStepQuery(search: string): boolean {
  const stepValues = new URLSearchParams(search).getAll("step");

  return (
    stepValues.length === 0 ||
    (stepValues.length === 1 && VALID_STEP_QUERY_VALUES.has(stepValues[0]!))
  );
}

function formatSelectedTerm(date: Date, timezone: string) {
  const zonedDate = toZonedTime(date, timezone);
  const time = format(zonedDate, "HH:mm", { locale: srLatn });

  if (isToday(zonedDate)) return `danas u ${time}`;
  if (isTomorrow(zonedDate)) return `sutra u ${time}`;

  return format(zonedDate, "EEEE, d. MMMM 'u' HH:mm", { locale: srLatn });
}

function BookingBreadcrumbs({
  therapist,
}: {
  therapist: SessionBookingProps["therapist"];
}) {
  return (
    <div className="mb-2 md:mb-8 md:px-8">
      <Link
        href={`/terapeuti/${therapist.slug}`}
        className="flex items-center gap-1 text-sm md:hidden"
      >
        <ChevronLeft className="h-4 w-4" />
        Nazad na profil
      </Link>
      <Breadcrumb className="hidden md:block">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/">Početna</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/terapeuti">Naši terapeuti</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href={`/terapeuti/${therapist.slug}`}>
              {therapist.user.name}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Rezervacija termina</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
}

function BookingHeading({
  therapist,
  isIntroCall,
  children,
}: {
  therapist: SessionBookingProps["therapist"];
  isIntroCall: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="md:px-8 md:pt-8">
      <TypographyH1 variant="product">
        {isIntroCall
          ? "Izaberi termin za uvodni razgovor"
          : "Izaberi termin za seansu"}
      </TypographyH1>
      <div className="flex flex-col gap-2">
        <TypographyMuted>
          {therapist.user.name}, {therapist.title} sa{" "}
          {therapist.yearsOfExperience} godina iskustva
        </TypographyMuted>
        {children}
      </div>
    </div>
  );
}

function TherapyTypeToggle({
  therapist,
  therapyType,
  onChange,
  hasMultipleTherapyTypes,
}: {
  therapist: SessionBookingProps["therapist"];
  therapyType: TherapyRelationshipType;
  onChange?: (type: TherapyRelationshipType) => void;
  hasMultipleTherapyTypes: boolean;
}) {
  const isIndividualActive = therapyType === TherapyRelationshipType.INDIVIDUAL;
  const isCouplesActive = therapyType === TherapyRelationshipType.COUPLES;

  return (
    <div
      role="radiogroup"
      aria-label="Vrsta terapije"
      className={cn(
        "mt-4 flex",
        hasMultipleTherapyTypes
          ? "w-full rounded-full bg-secondary p-1 md:w-fit"
          : "w-fit",
      )}
    >
      {onChange ? (
        <Button
          type="button"
          role="radio"
          aria-checked={isIndividualActive}
          aria-label="Individualna terapija"
          variant="ghost"
          size="sm"
          onClick={() => onChange(TherapyRelationshipType.INDIVIDUAL)}
          className={cn(
            "flex items-center justify-center gap-2 rounded-full bg-transparent",
            hasMultipleTherapyTypes && "flex-1 md:flex-none",
            isIndividualActive &&
              hasMultipleTherapyTypes &&
              "bg-background shadow-xs hover:bg-background",
          )}
        >
          <Armchair className="h-4 w-4" />
          <span className="md:hidden">Individualna</span>
          <span className="hidden md:inline">Individualna terapija</span>
        </Button>
      ) : (
        <Link
          href={`/terapeuti/${therapist.slug}/book?therapyType=${TherapyRelationshipType.INDIVIDUAL}`}
          aria-current={isIndividualActive ? "true" : undefined}
          className={cn(
            buttonVariants({ variant: "ghost", size: "sm" }),
            "flex rounded-full bg-transparent",
            hasMultipleTherapyTypes && "flex-1 md:flex-none",
            isIndividualActive &&
              hasMultipleTherapyTypes &&
              "bg-background shadow-xs hover:bg-background",
          )}
        >
          <Armchair className="h-4 w-4" />
          <span className="md:hidden">Individualna</span>
          <span className="hidden md:inline">Individualna terapija</span>
        </Link>
      )}
      {therapist.couplesTherapyPrice &&
        (onChange ? (
          <Button
            type="button"
            role="radio"
            aria-checked={isCouplesActive}
            aria-label="Terapija za parove"
            variant="ghost"
            size="sm"
            onClick={() => onChange(TherapyRelationshipType.COUPLES)}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 rounded-full bg-transparent md:flex-none",
              isCouplesActive && "bg-background shadow-xs hover:bg-background",
            )}
          >
            <Sofa className="h-4 w-4" />
            <span className="md:hidden">Za parove</span>
            <span className="hidden md:inline">Terapija za parove</span>
          </Button>
        ) : (
          <Link
            href={`/terapeuti/${therapist.slug}/book?therapyType=${TherapyRelationshipType.COUPLES}`}
            aria-current={isCouplesActive ? "true" : undefined}
            className={cn(
              buttonVariants({ variant: "ghost", size: "sm" }),
              "flex flex-1 rounded-full bg-transparent md:flex-none",
              isCouplesActive && "bg-background shadow-xs hover:bg-background",
            )}
          >
            <Sofa className="h-4 w-4" />
            <span className="md:hidden">Za parove</span>
            <span className="hidden md:inline">Terapija za parove</span>
          </Link>
        ))}
    </div>
  );
}

function StepHeaderLabel({
  label,
  selectedTermDescription,
}: {
  label: string;
  selectedTermDescription?: string;
}) {
  if (label === "Termin" && selectedTermDescription) {
    return (
      <span className="inline-flex items-baseline gap-2">
        <span>Termin</span>
        <span className="text-muted-foreground">{selectedTermDescription}</span>
      </span>
    );
  }

  return <>{label}</>;
}

function StepIdentityFallback() {
  return (
    <div
      className="flex w-full flex-col items-center space-y-4 p-8"
      role="status"
      aria-live="polite"
      aria-label="Učitavanje tvojih podataka"
    >
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-32 w-full max-w-md" />
      <p className="text-sm text-muted-foreground">
        Učitavamo tvoje podatke...
      </p>
    </div>
  );
}

function StepIdentity({
  attribution,
  therapist,
  sessionPurpose,
  therapyType,
}: {
  attribution: Promise<BookingAttribution>;
  therapist: SessionBookingProps["therapist"];
  sessionPurpose: TherapySessionPurpose;
  therapyType: TherapyRelationshipType;
}) {
  const resolved = use(attribution);
  const { status } = useSession();

  // Identity and checkout must follow the same live session. The server
  // attribution snapshot does not refresh when shallow step queries change.
  if (status === "loading") return <StepIdentityFallback />;

  return status === "authenticated" ? (
    <StepLoginAuthorised
      therapist={therapist}
      attributionBridgeId={resolved.attributionBridgeId}
      sessionPurpose={sessionPurpose}
      therapyType={therapyType}
    />
  ) : (
    <StepLogin
      therapist={therapist}
      attributionBridgeId={resolved.attributionBridgeId}
      sessionPurpose={sessionPurpose}
      therapyType={therapyType}
    />
  );
}

function StepPaymentResolved({
  attribution,
  therapist,
}: {
  attribution: Promise<BookingAttribution>;
  therapist: SessionBookingProps["therapist"];
}) {
  const resolved = use(attribution);

  return (
    <StepPayment
      therapist={therapist}
      attributionBridgeId={resolved.attributionBridgeId}
    />
  );
}

function StepPaymentFallback({
  therapist,
  therapyType,
  formattedDateTime,
}: {
  therapist: SessionBookingProps["therapist"];
  therapyType: TherapyRelationshipType;
  formattedDateTime: string;
}) {
  return (
    <PaymentConfirmationLoadingShell
      therapyType={therapyType}
      therapistName={therapist.user.name ?? ""}
      therapistImage={therapist.user.image ?? undefined}
      formattedDateTime={formattedDateTime}
      sessionDurationMinutes={
        therapyType === TherapyRelationshipType.COUPLES
          ? therapist.couplesSessionDuration
          : therapist.individualSessionDuration
      }
    />
  );
}

function StepPaymentReturnFallback() {
  return (
    <div
      className="flex min-h-[400px] flex-col items-center justify-center gap-4 px-6"
      role="status"
      aria-label="Učitavanje potvrde plaćanja"
    >
      <Skeleton className="h-12 w-12 rounded-full" />
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-64" />
      <span className="sr-only">Učitavanje...</span>
    </div>
  );
}

function StepPaymentReturnResolved({
  attribution,
  therapistName,
  therapistId,
}: {
  attribution: Promise<BookingAttribution>;
  therapistName: string;
  therapistId: string;
}) {
  const resolved = use(attribution);

  return (
    <StepPaymentReturn
      therapistName={therapistName}
      therapistId={therapistId}
      isLogged={resolved.isLogged}
    />
  );
}

function BookingAttributionTracking({
  attribution,
  itemId,
  itemName,
  value,
}: {
  attribution: Promise<BookingAttribution>;
  itemId: string;
  itemName: string;
  value: number;
}) {
  const resolved = use(attribution);

  return (
    <ViewItemTracking
      itemId={itemId}
      itemName={itemName}
      value={value}
      attributionBridgeId={resolved.attributionBridgeId}
    />
  );
}

export default function SessionBooking(props: SessionBookingProps) {
  const hasMultipleTherapyTypes = Boolean(props.therapist.couplesTherapyPrice);
  const { value: isDesktop, isResolved: isViewportResolved } =
    useResolvedMediaQuery("(min-width: 768px)");
  const [stepQuery, setStepQuery] = useQueryState(
    "step",
    parseAsInteger.withDefault(props.initialStep),
  );
  const hasValidClientStepQuery =
    typeof window === "undefined" || hasValidStepQuery(window.location.search);
  const currentStep = hasValidClientStepQuery ? clampStep(stepQuery) : MIN_STEP;
  const [therapyTypeQuery, setTherapyTypeQuery] = useQueryState(
    "therapyType",
    parseAsStringEnum<TherapyRelationshipType>(
      Object.values(TherapyRelationshipType),
    ).withDefault(props.therapyType),
  );
  const sessionPurpose = props.sessionPurpose ?? TherapySessionPurpose.THERAPY;
  const isIntroCall = sessionPurpose === TherapySessionPurpose.INTRO_CALL;
  const [isProposed] = useQueryState(
    "proposed",
    parseAsBoolean.withDefault(false),
  );
  const [proposalId] = useQueryState("proposalId", parseAsInteger);
  const [clientTimezone, setClientTimezone] = useState<string | null>(null);
  const [dateQuery, setDateQuery] = useQueryState("date", parseAsIsoDateTime);

  useEffect(() => {
    setClientTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, []);

  const selectedTermDescription = useMemo(() => {
    if (!dateQuery || !clientTimezone) return undefined;

    return formatSelectedTerm(dateQuery, clientTimezone);
  }, [dateQuery, clientTimezone]);

  useEffect(() => {
    if (!hasValidStepQuery(window.location.search)) {
      void setStepQuery(MIN_STEP);
    }
  }, [setStepQuery]);

  const steps: BookingStep[] = [
    {
      label: "Termin",
      content: (
        <StepTime
          userId={props.therapist.userId}
          therapist={props.therapist}
          sessionPurpose={sessionPurpose}
        />
      ),
    },
    {
      label: "Tvoji podaci",
      content: (
        <Suspense fallback={<StepIdentityFallback />}>
          <StepIdentity
            attribution={props.attribution}
            therapist={props.therapist}
            sessionPurpose={sessionPurpose}
            therapyType={therapyTypeQuery}
          />
        </Suspense>
      ),
    },
    {
      label: isIntroCall ? "Potvrda" : "Plaćanje",
      content: isIntroCall ? (
        <StepIntroCallConfirm therapist={props.therapist} />
      ) : (
        <Suspense
          fallback={
            <StepPaymentFallback
              therapist={props.therapist}
              therapyType={therapyTypeQuery}
              formattedDateTime={selectedTermDescription ?? "Izabrani termin"}
            />
          }
        >
          <StepPaymentResolved
            attribution={props.attribution}
            therapist={props.therapist}
          />
        </Suspense>
      ),
    },
  ];

  const [showTimeWarning, setShowTimeWarning] = useState(false);
  // Defers mounting step-1 content until the viewport (mobile vs desktop)
  // has resolved. Without this, step-1 content mounts once for the
  // server-guessed layout and again when the real viewport resolves,
  // double-firing its one-shot tracking effects (add_to_cart etc).
  const shouldDeferStepContent = !isViewportResolved && currentStep === 1;

  const handleTherapyTypeChange = (newType: TherapyRelationshipType) => {
    const isSwitchingType = newType !== therapyTypeQuery;

    // A previously selected slot may no longer be valid: availability, duration,
    // and price all differ per therapy type. Force the user to re-pick a slot.
    if (isSwitchingType && dateQuery) {
      void setDateQuery(null);
      void setStepQuery(0);
    }

    void setTherapyTypeQuery(newType);
  };

  const handleStepClick = (index: number) => {
    if (index > currentStep) return;

    void setStepQuery(index);
  };

  // Validate booking time: use therapist's configured minimum booking lead time
  // Note: canReschedule validation is NOT used here - that's for managing
  // existing sessions in the chat/inbox view, not for new bookings.
  const isCorrectTime = useMemo(() => {
    if (!dateQuery) return true;
    if (isProposed || proposalId != null) return true;

    const now = new Date();
    const sessionDate = new Date(dateQuery);
    const minimumLeadTime = props.therapist.minimumBookingLeadTime;
    const firstAvailableSessionTime = addHours(now, minimumLeadTime);
    return sessionDate.getTime() > firstAvailableSessionTime.getTime();
  }, [
    dateQuery,
    isProposed,
    proposalId,
    props.therapist.minimumBookingLeadTime,
  ]);

  useEffect(() => {
    setShowTimeWarning(!isCorrectTime);
  }, [isCorrectTime]);

  const attributionTracking = shouldDeferStepContent ? null : (
    <Suspense fallback={null}>
      <BookingAttributionTracking
        attribution={props.attribution}
        itemId={`book-with-therapist-${props.therapist.id}`}
        itemName={props.therapist.user.name ?? ""}
        value={props.therapist.defaultPrice ?? 0}
      />
    </Suspense>
  );

  // IMPORTANT: Step 3 (success page) check MUST come BEFORE TimeWarning check.
  // This prevents showing "slot unavailable" error after a successful payment.
  // Bug fix: A customer who books 18-24h ahead passes the 18h check as a new customer,
  // but after payment creates a relationship, the 24h check would fail.
  // The success page should always be shown after successful payment.
  if (currentStep === 3) {
    return (
      <>
        {attributionTracking}
        <div className="min-h-screen w-full">
          <Suspense fallback={<StepPaymentReturnFallback />}>
            <StepPaymentReturnResolved
              attribution={props.attribution}
              therapistName={props.therapist.user.name ?? ""}
              therapistId={props.therapist.id}
            />
          </Suspense>
        </div>
      </>
    );
  }

  const activeStepContent = showTimeWarning ? (
    <TimeWarning date={dateQuery ?? undefined} therapist={props.therapist} />
  ) : (
    steps[currentStep]?.content
  );
  const visibleStepContent = shouldDeferStepContent ? (
    <StepIdentityFallback />
  ) : (
    activeStepContent
  );

  // Step 2 (Plaćanje / Potvrda) is a self-contained checkout screen with its
  // own header, summary, and price. Suppress the surrounding booking chrome
  // (breadcrumbs, heading, therapy type toggle, summary strip) so the price
  // and therapist aren't rendered multiple times. The step headers stay as
  // the single controlled way back to steps 0/1.
  const isCheckoutStep = currentStep === 2;

  return (
    <div
      className={cn(
        "container mx-auto max-w-5xl px-3 md:pt-12",
        !isCheckoutStep && "pt-4",
      )}
    >
      {attributionTracking}
      {!isCheckoutStep && (
        <>
          <BookingBreadcrumbs therapist={props.therapist} />
          <BookingHeading therapist={props.therapist} isIntroCall={isIntroCall}>
            {!isIntroCall && (
              <TherapyTypeToggle
                therapist={props.therapist}
                therapyType={therapyTypeQuery}
                hasMultipleTherapyTypes={hasMultipleTherapyTypes}
                onChange={handleTherapyTypeChange}
              />
            )}
          </BookingHeading>
          <div className="mt-4 md:px-8">
            <BookingSummary
              therapist={props.therapist}
              therapyType={therapyTypeQuery}
              isIntroCall={isIntroCall}
              selectedTerm={selectedTermDescription}
            />
          </div>
        </>
      )}
      <div
        className={cn(
          "flex w-full flex-col gap-4 pb-8 md:px-8 md:pt-8",
          isCheckoutStep ? "pt-6" : "pt-4",
        )}
      >
        {isCheckoutStep ? (
          visibleStepContent && (
            <div className="min-h-96">{visibleStepContent}</div>
          )
        ) : (
          <div data-testid="booking-stepper">
            {isDesktop ? (
              <>
                <div className="grid grid-cols-3 gap-4">
                  {steps.map(({ label }, index) => {
                    const isActive = index <= currentStep;

                    return (
                      <button
                        key={label}
                        type="button"
                        disabled={index > currentStep}
                        aria-current={
                          index === currentStep ? "step" : undefined
                        }
                        className={cn(
                          "min-w-0 border-t-[3px] pt-2 text-left text-sm font-medium transition-opacity disabled:cursor-default disabled:opacity-80",
                          isActive && "border-primary",
                        )}
                        onClick={() => handleStepClick(index)}
                      >
                        <StepHeaderLabel
                          label={label}
                          selectedTermDescription={selectedTermDescription}
                        />
                      </button>
                    );
                  })}
                </div>

                {visibleStepContent && (
                  <div
                    className={cn(
                      "mb-4 mt-4",
                      "flex items-center justify-center rounded-md border bg-background",
                      steps[currentStep]?.label === "Tvoji podaci"
                        ? "min-h-0"
                        : "min-h-96",
                    )}
                  >
                    {visibleStepContent}
                  </div>
                )}
              </>
            ) : (
              <div>
                <ol
                  className="grid grid-cols-3 border-b pb-3"
                  aria-label="Koraci rezervacije"
                >
                  {steps.map(({ label }, index) => {
                    const isCurrentStep = index === currentStep;
                    const isComplete = index < currentStep;
                    const mobileLabel =
                      label === "Tvoji podaci" ? "Podaci" : label;

                    return (
                      <li key={label} className="min-w-0">
                        <button
                          type="button"
                          disabled={index > currentStep}
                          aria-current={isCurrentStep ? "step" : undefined}
                          data-testid={`booking-step-${index}`}
                          className={cn(
                            "inline-flex w-full min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-full px-1 py-1 text-xs font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-default",
                            isCurrentStep &&
                              "bg-brand-action px-2.5 text-white",
                            isComplete && "text-foreground hover:text-brand",
                            index > currentStep && "text-muted-foreground/60",
                          )}
                          onClick={() => handleStepClick(index)}
                        >
                          <span
                            className={cn(
                              "flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
                              isCurrentStep && "bg-white/20 text-white",
                              isComplete && "bg-brand-action text-white",
                              index > currentStep &&
                                "bg-muted text-muted-foreground",
                            )}
                          >
                            {isComplete ? (
                              <Check className="h-3 w-3" aria-hidden="true" />
                            ) : (
                              index + 1
                            )}
                          </span>
                          <span className="truncate">{mobileLabel}</span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
                {visibleStepContent && (
                  <div
                    className={cn(
                      "mt-4 flex items-center justify-center rounded-md border bg-background",
                      steps[currentStep]?.label === "Tvoji podaci"
                        ? "min-h-0"
                        : "min-h-96",
                    )}
                  >
                    {visibleStepContent}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
